/**
 * Represents the status of a user in the self-guided system.
 * This class provides predefined statuses and ensures that only one instance
 * of each status is created and reused.
 */
export default class UserStatus {
  constructor(name) {
    this.name = name;
  }

  /**
   * Gets the instance representing the "User Not Verified" status.
   * This status indicates that the user has not yet completed the verification process in self-guided system.
   *
   * @returns {UserStatus} An instance representing the "UserNotVerified" status.
   */
  static get NotVerified() {
    return this.getInstance('UserNotVerified');
  }

  /**
   * Represents the status when a user is not found in the self-guided system.
   * @returns {UserStatus} The instance corresponding to the 'UserNotFound' status.
   */
  static get NotFound() {
    return this.getInstance('UserNotFound');
  }

  /**
   * Retrieves the instance representing the 'user_verified' status.
   * This status indicates that the user has been successfully verified in self-guided system.
   *
   * @returns {UserStatus} The instance corresponding to the 'UserVerified' status.
   */
  static get Verified() {
    return this.getInstance('UserVerified');
  }

  /**
   * Retrieves the instance representing the 'UserBlocked' status.
   * This status indicates that the user has been blocked in self guided system.
   *
   * @returns {UserStatus} The instance corresponding to the 'UserBlocked' status.
   */
  static get UserBlocked() {
    return this.getInstance('UserBlocked');
  }

  static fromKey(key) {
    const statusMap = {
      UserNotVerified: UserStatus.NotVerified,
      UserNotFound: UserStatus.NotFound,
      UserVerified: UserStatus.Verified,
      UserBlocked: UserStatus.UserBlocked,
    };
    return statusMap[key];
  }

  /**
   * This function returns an instance of the UserStatus class based on a given key, creating a new
   * instance if one does not already exist.
   *
   * @param key - a unique identifier used to create and retrieve instances of the UserStatus class.
   * It is used to ensure that only one instance of the UserStatus class is created for each unique key value.
   *
   * @returns returns an instance of the `UserStatus` class with the specified `key`.
   * If an instance with the specified `key` already exists, it returns that instance instead of creating a new one.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new UserStatus(key);
    }
    return this.instances[key];
  }

  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(UserStatus, 'instances', { value: {} });
